import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, FileText, Building2, User, Phone, Mail, MapPin, Calendar, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { usePublicFetch } from '../hooks/usePublicFetch';
import { ErreurPublique } from '../components/public/ErreurPublique'; // 🚨 erreur honnête (404 / 410 / technique)
import { signalerEvenementPublic } from '../lib/suiviPublic'; // 📬 Suivi client

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export default function SoumissionPublique() {
  const { token } = useParams();
  const { data, loading, error, refetch } = usePublicFetch(
    `/api/public/soumission/${token}`,
    {
      fallbackPath: `/api/public/soumission/${token}/view`,
      notFoundMsg: "Cette soumission n'existe pas ou le lien est invalide.",
    }
  );
  const [showSignature, setShowSignature] = useState(false);
  const [signatureNom, setSignatureNom] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const signatureSectionRef = useRef(null);

  // 🆕 [Phase 70 — 2026-06-18] Capture côté navigateur via html2pdf.js
  // 🆕 [Phase 78 — 2026-06-22] Détection mobile + fallback impression (pattern Phase 77 Fix 1)
  const pdfRootRef = useRef(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // 🆕 [Phase 78 — 2026-06-22] Anti dark mode Samsung : meta theme-color blanc
  // injecté via useEffect (preserve l'état précédent au unmount).
  useEffect(() => {
    const existing = document.querySelector('meta[name="theme-color"]');
    const created = !existing;
    let meta = existing;
    if (created) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    const previousContent = meta.getAttribute('content');
    meta.setAttribute('content', '#ffffff');
    return () => {
      if (created) {
        document.head.removeChild(meta);
      } else if (previousContent !== null) {
        meta.setAttribute('content', previousContent);
      }
    };
  }, []);

  const handleDownloadPdf = async () => {
    if (!pdfRootRef.current || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const numero = (data?.soumission?.numero_soumission || data?.soumission?.numero || 'Soumission').replace(/[^a-z0-9\-_]/gi, '_');
      // 🆕 [Phase 78 — 2026-06-22] Détection mobile pour adapter `scale`.
      // Sur Samsung Android, scale:2 + windowWidth:1024 → canvas trop gros = OOM
      // Fix : scale 1.5 sur mobile, 2 sur desktop. Pas de windowHeight (auto).
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      await html2pdf()
        .from(pdfRootRef.current)
        .set({
          margin: [10, 10, 10, 10],
          filename: `Soumission_${numero}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: isMobile ? 1.5 : 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: 1024,
          },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save();
      signalerEvenementPublic(`/api/public/soumission/${token}/evenement`, 'pdf');  // 📬 Suivi client
    } catch (e) {
      console.error('[Phase 78] Erreur html2pdf:', e);
      // 🆕 [Phase 78] Message clair FR + fallback impression navigateur.
      toast.error("Le PDF n'a pas pu être généré sur cet appareil — utilisez Imprimer (puis « Enregistrer en PDF »).", { duration: 8000 });
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (data?.soumission?.signature_obtenue) setSigned(true);
  }, [data]);

  // 🆕 [Phase 79 — 2026-06-22] Canvas DPR-aware : on stocke le rect calculé au
  // setup pour que les coordonnées tactile/souris soient toujours en CSS px,
  // tandis que le buffer canvas est en device px (haute résolution).
  // La conversion CSS→device est faite par `ctx.scale(dpr, dpr)`, donc on
  // utilise directement les coords CSS dans `moveTo`/`lineTo`.
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY;
    if (clientX == null || clientY == null) return null;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    // 🆕 [Phase 79] Bloque le scroll de page pendant la signature mobile.
    // onTouch* React listeners sont non-passive par défaut → preventDefault OK.
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(e);
    if (!point) return;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    // 🆕 [Phase 79] Bloque aussi le scroll pendant le déplacement du doigt.
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(e);
    if (!point) return;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // 🆕 [Phase 79] On efface en CSS px (l'effet `ctx.scale(dpr, dpr)` du
    // useEffect setup mappe automatiquement vers le buffer device px).
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  const handleClickSigner = () => {
    setShowSignature(true);
    setTimeout(() => {
      signatureSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSign = async () => {
    if (!signatureNom.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasSignature = imageData.data.some((pixel, i) => i % 4 !== 3 && pixel !== 255);

    if (!hasSignature) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    setSigning(true);
    try {
      const signatureData = canvas.toDataURL('image/png');
      const res = await axios.post(`${API_URL}/api/public/soumission/${token}/signer`, {
        signature_data: signatureData,
        signature_nom: signatureNom.trim()
      });

      if (res.status !== 200) {
        const err = res.data || {};  // 🆕 Phase E2 — fix copy-paste axios (data déjà parsé)
        throw new Error(err.detail || 'Erreur lors de la signature');
      }

      toast.success('Soumission signee avec succes! Merci pour votre confiance.');
      setSigned(true);
      setShowSignature(false);
      refetch();
    } catch (e) {
      // 🆕 [5.4] Safeguard signature : si 409 → conflit nacelle, propose alternatives ou bloque
      if (e?.response?.status === 409 && e?.response?.data?.detail?.conflict) {
        const d = e.response.data.detail;
        const alts = d.alternatives || [];
        if (alts.length > 0) {
          const liste = alts.slice(0, 3).map(a => `• ${a.nom}`).join('\n');
          toast.error(
            `${d.message}\n\nAlternatives libres :\n${liste}\n\nMerci de contacter votre représentant pour qu'il modifie la soumission avant signature.`,
            { duration: 12000 }
          );
        } else {
          toast.error(
            `${d.message}\n\nAucune alternative disponible pour ces dates. Merci de contacter votre représentant.`,
            { duration: 12000 }
          );
        }
      } else {
        toast.error(e?.response?.data?.detail || e.message || 'Erreur lors de la signature');
      }
    } finally {
      setSigning(false);
    }
  };

  // 🆕 [Phase 79 — 2026-06-22] Setup canvas haute résolution (devicePixelRatio).
  // Le buffer canvas est en device px (rect.width * dpr), le style en CSS px.
  // `ctx.scale(dpr, dpr)` mappe les coords CSS aux pixels device → trait net.
  // Resize listener pour recalculer quand l'utilisateur tourne son téléphone.
  useEffect(() => {
    if (!showSignature) return undefined;

    const CANVAS_HEIGHT_CSS = 140;  // ↑ légèrement plus haut qu'avant (120 → 140)
    const setupCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0) return;  // pas encore rendu
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = CANVAS_HEIGHT_CSS * dpr;
      canvas.style.height = `${CANVAS_HEIGHT_CSS}px`;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, CANVAS_HEIGHT_CSS);
      ctx.strokeStyle = '#0f172a';  // slate-900 → plus contrasté que le noir pur
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    // Setup initial après le mount du canvas
    const timeoutId = setTimeout(setupCanvas, 50);

    // Resize : repaint à la rotation device ou redimension fenêtre.
    // ATTENTION : ça efface la signature en cours. Comportement acceptable
    // car l'utilisateur n'attend généralement pas à signer + tourner son téléphone.
    window.addEventListener('resize', setupCanvas);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', setupCanvas);
    };
  }, [showSignature]);

  const formatMontant = (val) => {
    return (val || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // 🆕 [Phase 63] Visualiser en PDF — Génération backend via Playwright (rendu pixel-perfect)
  // Remplace l'ancien window.print() qui ne respectait jamais le format papier.
  const handlePrint = () => {
    const token = window.location.pathname.split('/').pop();
    window.open(`${API_URL}/api/pdf/soumission/${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement de la soumission...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErreurPublique error={error} doc="Soumission" onRetry={refetch} />
      </div>
    );
  }

  if (data?._fallbackHtml) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <Card className="mb-4 border-amber-300 bg-amber-50">
            <CardContent className="pt-4">
              <p className="text-amber-800 text-sm">
                La plateforme est temporairement en maintenance. Voici votre soumission en mode lecture seule.
              </p>
            </CardContent>
          </Card>
          <div dangerouslySetInnerHTML={{ __html: data._fallbackHtml }} />
        </div>
      </div>
    );
  }

  const { soumission, client, dossier, entreprise, documents } = data || {};

  return (
    <>
      {/* Print CSS: garder les bordures des Cards identiques a l'ecran */}
      <style>{`
        @media print {
          @page { size: letter; margin: 12mm; }
          .no-print, .no-print * { display: none !important; }
          html, body {
            height: auto !important; overflow: visible !important;
            font-size: 10px !important;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
            margin: 0 !important; padding: 0 !important; background: white !important;
          }
          #root { position: static !important; overflow: visible !important; height: auto !important; }
          .soum-screen-wrapper { min-height: auto !important; background: white !important; padding: 0 !important; }
          .soum-screen-wrapper > div { max-width: none !important; padding: 0 !important; }
          /* Garder les bordures Card a l'impression */
          [data-slot="card"] {
            border: 1px solid #e5e7eb !important;
            border-radius: 0.75rem !important;
            box-shadow: none !important;
            margin-bottom: 8px !important;
          }
          /* Masquer le footer URL a l'impression */
          .soum-footer-print-hide { display: none !important; }
          /* Masquer le badge Emergent */
          [data-emergent-badge], #emergent-badge, .emergent-badge,
          a[href*="emergentagent"], a[href*="emergent"],
          div[style*="position: fixed"], div[style*="position:fixed"] {
            display: none !important; visibility: hidden !important;
          }
        }
        /* Rich text content from Tiptap editor */
        .soum-riche p { margin: 0 0 8px 0; }
        .soum-riche h1 { font-size: 24px; font-weight: 700; margin: 16px 0 8px 0; }
        .soum-riche h2 { font-size: 20px; font-weight: 700; margin: 14px 0 6px 0; }
        .soum-riche h3 { font-size: 17px; font-weight: 600; margin: 12px 0 6px 0; }
        .soum-riche strong { font-weight: 700; }
        .soum-riche em { font-style: italic; }
        .soum-riche u { text-decoration: underline; }
        .soum-riche ul { padding-left: 20px; margin: 8px 0; list-style-type: disc; }
        .soum-riche ol { padding-left: 20px; margin: 8px 0; list-style-type: decimal; }
        .soum-riche li { margin: 4px 0; }
        .soum-riche blockquote { border-left: 3px solid #d1d5db; padding-left: 12px; margin: 8px 0; color: #4b5563; }

        /* 🆕 [Phase 78 — 2026-06-22] Anti-badge Emergent SCOPED à la soumission publique
           uniquement (preserve le badge sur pages internes admin → respecte ToS Emergent).
           Triple verrou : display + visibility + pointer-events. */
        .soum-screen-wrapper #emergent-badge,
        .soum-screen-wrapper [id^="emergent-"],
        .soum-screen-wrapper [data-emergent-badge],
        body > #emergent-badge,
        body > [id^="emergent-"],
        body > [data-emergent-badge],
        a[href*="emergentagent.com"],
        a[href*="emergent.sh"],
        a[href*="app.emergent.sh"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>

      <div
        className="soum-screen-wrapper min-h-screen bg-gray-50 py-6 sm:py-8 px-3 sm:px-4 text-slate-900"
        style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
        data-pdf-ready="1"
        data-testid="soumission-publique-root"
      >
        <div className="max-w-4xl mx-auto">

          {/* 🆕 [Phase 70 follow-up — 2026-06-18] Barre de boutons EN DEHORS du
              ref `pdfRootRef` → n'apparaît PAS dans le PDF généré.
              🆕 [Phase 78 — 2026-06-22] Stack mobile + full-width + min-h-[48px] HIG */}
          <div className="no-print flex flex-col sm:flex-row sm:justify-end gap-2 mb-4">
            {!signed && !showSignature && (
              <Button
                onClick={handleClickSigner}
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto min-h-[48px]"
                data-testid="sign-btn-top"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Signer
              </Button>
            )}
            <Button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto min-h-[48px]"
              data-testid="btn-download-pdf"
            >
              {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {downloadingPdf ? 'Génération…' : '📥 Télécharger en PDF'}
            </Button>
          </div>

          {/* Tout ce qui suit est dans le ref → INCLUS dans le PDF */}
          <div ref={pdfRootRef} data-testid="pdf-content-root">

          {/* Header Entreprise — Layout original 3 colonnes (responsive : stack vertical en mobile)
              🆕 [Phase 78 — 2026-06-22] Fallback logo PRQC vert sobre + support logo_principal
              (consomme la migration Phase 77 Fix 1 si l'endpoint expose `logo_principal`). */}
          <Card className="mb-2 bg-white text-slate-900" data-testid="header-card">
            <CardContent className="p-0 overflow-hidden">
              <div
                className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] md:min-h-[180px]"
                data-testid="header-grid"
              >
                {/* Logo — colonne gauche, fond gris, prend toute la hauteur */}
                <div className="bg-gray-100 flex items-center justify-center p-4 md:p-4">
                  {(() => {
                    const logoUrl = entreprise?.logo_url || (entreprise?.logo_principal ? `${API_URL}${entreprise.logo_principal}` : null);
                    if (logoUrl) {
                      return (
                        <img
                          src={logoUrl}
                          alt="Logo"
                          className="max-h-24 md:max-h-none w-auto md:w-full h-auto object-contain"
                          style={{ maxWidth: '180px' }}
                          data-testid="header-logo"
                        />
                      );
                    }
                    return (
                      <div
                        className="text-2xl md:text-3xl font-bold text-emerald-700 leading-tight text-center"
                        data-testid="header-logo-fallback"
                      >
                        Peinture<br />Résidentielle<br />Qc Inc.
                      </div>
                    );
                  })()}
                </div>
                {/* Infos entreprise — colonne centre */}
                <div className="flex flex-col items-center justify-center py-4 px-4 md:px-6 text-center border-t md:border-t-0 break-words">
                  <h1 className="text-lg md:text-xl font-bold text-slate-900 mb-2">{entreprise?.nom_entreprise}</h1>
                  <div className="text-sm leading-relaxed text-slate-700 space-y-0.5">
                    <p>{entreprise?.adresse}</p>
                    <p>{entreprise?.ville} {entreprise?.code_postal}</p>
                    {entreprise?.telephone && (
                      <p className="font-semibold mt-1">
                        <a href={`tel:${entreprise.telephone}`} className="text-slate-900 hover:text-blue-600 transition no-underline">{entreprise.telephone}</a>
                      </p>
                    )}
                    {entreprise?.email && (
                      <p className="break-all">
                        <a href={`mailto:${entreprise.email}`} className="text-slate-700 hover:text-blue-600 transition no-underline">{entreprise.email}</a>
                      </p>
                    )}
                    {entreprise?.site_web && <p className="break-all">{entreprise.site_web}</p>}
                  </div>
                </div>
                {/* Infos dossier — colonne droite (devient pleine largeur en mobile) */}
                <div className="py-4 px-4 md:px-5 text-sm border-t md:border-t-0 md:border-l flex flex-col justify-center break-words">
                  <p className="font-bold text-slate-900 mb-1">
                    Dossier: {dossier?.numero_dossier}
                  </p>
                  {dossier?.representant_nom && (
                    <p className="text-slate-600 mb-0.5">Cree par: {dossier.representant_nom}{dossier.representant_telephone ? ` — ${dossier.representant_telephone}` : ''}</p>
                  )}
                  <p className="text-slate-600 mb-0.5">
                    Date: {new Date(soumission?.date_creation).toLocaleDateString('fr-CA')}
                  </p>
                  {soumission?.numero_soumission && (
                    <p className="text-slate-600 mb-2 break-all">{soumission.numero_soumission}</p>
                  )}
                  <div className="text-xs text-slate-500 pt-2 border-t space-y-0.5">
                    {entreprise?.numero_tps && <p className="break-all"><strong>TPS:</strong> {entreprise.numero_tps}</p>}
                    {entreprise?.numero_tvq && <p className="break-all"><strong>TVQ:</strong> {entreprise.numero_tvq}</p>}
                    {entreprise?.numero_rbq && <p className="break-all"><strong>RBQ:</strong> {entreprise.numero_rbq}</p>}
                    {entreprise?.numero_neq && <p className="break-all"><strong>NEQ:</strong> {entreprise.numero_neq}</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statut si signe */}
          {signed && (
            <Card className="mb-2 border-green-200 bg-green-50" data-testid="signed-banner">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 text-green-700">
                  <CheckCircle className="w-8 h-8" />
                  <div>
                    <p className="font-semibold text-lg">Soumission acceptee et signee</p>
                    <p className="text-sm">Signee par {soumission?.signature_client_nom} le {new Date(soumission?.date_signature).toLocaleDateString('fr-CA')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Client */}
          <Card className="mb-2 bg-white text-slate-900" data-testid="client-info-card">
            <CardHeader className="bg-gray-50 border-b py-3">
              <CardTitle className="text-base font-bold">Client</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Client :</p>
                  <p className="font-medium">{client?.nom}</p>
                  <p className="text-sm text-gray-600">Contact: {client?.nom}</p>
                  <p className="text-sm text-gray-600">Telephone: {client?.telephone || 'N/A'}</p>
                  <p className="text-sm text-gray-600">Courriel: {client?.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Adresse du client :</p>
                  <p className="font-medium">{client?.adresse_client || client?.adresse || '-'}</p>
                  <p className="text-sm text-gray-600">{client?.ville_client || ''} {client?.code_postal_client || ''}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Adresse des travaux :</p>
                  <p className="font-medium">{dossier?.adresse_travaux || '-'}</p>
                  <p className="text-sm text-gray-600">{dossier?.ville_travaux || ''} {dossier?.code_postal_travaux || ''}</p>
                </div>
                {soumission?.type_soumission === 'location' && soumission?.adresse_livraison && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Adresse de livraison :</p>
                    <p className="font-medium">{soumission.adresse_livraison}</p>
                    <p className="text-sm text-gray-600">{soumission.ville_livraison || ''} {soumission.code_postal_livraison || ''}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Description des travaux */}
          {soumission?.description_travaux && (
            <Card className="mb-2" data-testid="description-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Description des Travaux</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="border rounded-lg p-4 bg-gray-50/30">
                  <div className="soum-riche prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: soumission.description_travaux }} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {soumission?.notes && (
            <Card className="mb-2" data-testid="notes-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="border rounded-lg p-4 bg-gray-50/30">
                  <div className="soum-riche prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: soumission.notes }} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Items / Services */}
          {soumission?.items?.length > 0 && soumission.items.some(i => !i.type || i.type === 'service') && (
            <Card className="mb-2 bg-white text-slate-900" data-testid="services-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Services</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {/* 🆕 [Phase 78] Table desktop conservée — `windowWidth: 1024` la rendra dans le PDF */}
                <table className="hidden md:table w-full border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="p-3 text-left text-xs font-semibold uppercase">Description</th>
                      <th className="p-3 text-right w-32 text-xs font-semibold uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soumission.items.filter(i => !i.type || i.type === 'service').map((item, idx) => (
                      <tr key={item.id || item._id || `srv-${idx}`} className="border-b">
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right font-medium">{formatMontant(item.montant)} $</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* 🆕 [Phase 78] Cartes mobile empilées (md:hidden) — pas de scroll horizontal */}
                <div className="md:hidden space-y-3" data-testid="services-mobile-cards">
                  {soumission.items.filter(i => !i.type || i.type === 'service').map((item, idx) => (
                    <div key={item.id || item._id || `srv-mob-${idx}`} className="border border-slate-200 rounded-lg p-3 bg-white">
                      <p className="text-sm text-slate-800 break-words">{item.description}</p>
                      <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500 uppercase">Total</span>
                        <span className="text-sm font-semibold text-slate-900">{formatMontant(item.montant)} $</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 🆕 Produits (matériel) */}
          {soumission?.items?.some(i => i.type === 'produit') && (
            <Card className="mb-2 bg-white text-slate-900" data-testid="produits-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Produits</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Table desktop */}
                <table className="hidden md:table w-full border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="border-b bg-gray-100">
                      <th className="p-3 text-left text-xs font-semibold uppercase">Description</th>
                      <th className="p-3 text-right text-xs font-semibold uppercase w-20">Qté</th>
                      <th className="p-3 text-right text-xs font-semibold uppercase w-28">Prix unit.</th>
                      <th className="p-3 text-right text-xs font-semibold uppercase w-32">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soumission.items.filter(i => i.type === 'produit').map((item, idx) => (
                      <tr key={item.id || `prod-${idx}`} className="border-b">
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right">{item.quantite ?? 1}</td>
                        <td className="p-3 text-right">{formatMontant(item.prix_unitaire || 0)} $</td>
                        <td className="p-3 text-right font-medium">{formatMontant(item.montant)} $</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Cartes mobile */}
                <div className="md:hidden space-y-3" data-testid="produits-mobile-cards">
                  {soumission.items.filter(i => i.type === 'produit').map((item, idx) => (
                    <div key={item.id || `prod-mob-${idx}`} className="border border-slate-200 rounded-lg p-3 bg-white">
                      <p className="text-sm text-slate-800 break-words font-medium">{item.description}</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-2 mt-2 border-t border-slate-100 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Qté</span>
                          <span className="text-slate-800">{item.quantite ?? 1}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Prix unit.</span>
                          <span className="text-slate-800">{formatMontant(item.prix_unitaire || 0)} $</span>
                        </div>
                        <div className="col-span-2 flex justify-between pt-1 mt-1 border-t border-slate-100">
                          <span className="text-xs text-slate-500 uppercase">Total</span>
                          <span className="font-semibold text-slate-900">{formatMontant(item.montant)} $</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 🆕 2026-05 — Équipements loués : sections détaillées par équipement
              (Location / Livraison / Extras), avec autant de détails que la vue interne. */}
          {soumission?.items?.some(i => i.type === 'equipement') && (
            <Card className="mb-2" data-testid="equipements-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Équipements loués</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-6">
                {soumission.items.filter(i => i.type === 'equipement').map((it, i) => {
                  const dureeLabel = it.duree_type === 'semaine' ? 'semaine' : it.duree_type === 'mois' ? 'mois' : 'jour';
                  const dureeAbbrev = it.duree_type === 'semaine' ? 'sem' : it.duree_type === 'mois' ? 'mois' : 'jour';
                  const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
                  const qte = parseFloat(it.quantite) || 1;
                  const tarif = parseFloat(it.prix_location) || 0;
                  const livraison = parseFloat(it.prix_livraison || it.livraison || 0);
                  const livraisonBase = parseFloat(it.prix_livraison_base ?? it.livraison_base ?? 0);
                  const livraisonSupp = parseFloat(it.prix_livraison_supplement ?? it.livraison_supplement ?? 0);
                  const extras = (it.extras || []).filter(e => (e.description || e.nom) && (parseFloat(e.montant) || 0) > 0);
                  const extrasSum = extras.reduce((s, ex) => s + (parseFloat(ex.montant) || 0), 0);
                  const sousTotalLocation = tarif * qte;
                  const totalLigne = sousTotalLocation + livraison + extrasSum;
                  // 🔧 Bug #3 (2026-05-01) — Vue détaillée livraison alignée sur admin
                  const priseMode = it.prise_mode || 'livraison';           // 'livraison' | 'bureau'
                  const remiseMode = it.remise_mode || 'livraison';         // 'livraison' | 'bureau'
                  const nbTrajets = (priseMode === 'livraison' ? 1 : 0) + (remiseMode === 'livraison' ? 1 : 0);
                  const sensCalc = nbTrajets === 2 ? 'aller-retour' : nbTrajets === 1 ? 'aller seulement' : 'aucun trajet';
                  const sensLabel = nbTrajets === 2 ? 'Aller-retour (2 déplacements)'
                                   : nbTrajets === 1 ? 'Aller simple (1 déplacement)'
                                   : 'Aucun déplacement';
                  const modeLivraisonLabel = nbTrajets === 0
                    ? 'Prise au bureau (sans frais)'
                    : 'Livraison par PRQC';
                  const priseLabel = priseMode === 'livraison'
                    ? '🚚 Nous livrons chez vous'
                    : '🏢 Vous venez chercher au bureau (sans frais)';
                  const remiseLabel = remiseMode === 'livraison'
                    ? '🚚 Nous récupérons chez vous'
                    : '🏢 Vous ramenez au bureau (sans frais)';
                  const endroitLabelFmt = it.livraison_endroit === 'precise' ? 'Endroit précis'
                                       : it.livraison_endroit === 'adresse' ? 'Endroit normal (devant adresse)'
                                       : '';
                  const kmMontant = parseFloat(it.livraison_km_montant || 0);
                  const endroitMontant = parseFloat(it.livraison_endroit_montant || 0);
                  // Fallback si les sous-totaux détaillés ne sont pas présents
                  const kmMontantAff = kmMontant > 0 ? kmMontant : livraisonBase;
                  const endroitMontantAff = endroitMontant > 0 ? endroitMontant : livraisonSupp;
                  const baseDesc = it.description || it.nom || `Équipement #${i + 1}`;
                  return (
                    <div key={it.id || `eq-${i}`} className="border rounded-lg overflow-hidden" data-testid={`equipement-block-${i}`}>
                      {/* En-tête de l'équipement */}
                      <div className="bg-blue-50 border-b border-blue-100 px-4 py-3">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <div className="font-semibold text-gray-900">{baseDesc}</div>
                          <div className="text-xs text-gray-600">
                            Du <span className="font-medium">{fmtDate(it.date_prise)}</span> au <span className="font-medium">{fmtDate(it.date_remise)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Section LOCATION */}
                      <div className="px-4 py-3 border-b">
                        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">📅 Location</div>
                        <div className="flex justify-between items-baseline gap-3">
                          <div className="text-sm text-gray-800">
                            {tarif.toFixed(2)} $ / {dureeLabel} × {qte} {dureeAbbrev}{qte > 1 ? 's' : ''}
                          </div>
                          <div className="text-sm font-semibold whitespace-nowrap">{sousTotalLocation.toFixed(2)} $</div>
                        </div>
                      </div>

                      {/* Section LIVRAISON — détaillée alignée sur vue admin */}
                      {livraison > 0 && (
                        <div className="px-4 py-3 border-b" data-testid={`livraison-block-${i}`}>
                          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">🚚 Livraison</div>
                          <div className="space-y-2">
                            {/* Détail segmenté */}
                            <div className="space-y-1 text-sm">
                              <div className="flex items-start gap-2" data-testid={`liv-mode-${i}`}>
                                <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">Mode de livraison</span>
                                <span className="font-medium text-gray-800">{modeLivraisonLabel}</span>
                              </div>
                              <div className="flex items-start gap-2" data-testid={`liv-prise-${i}`}>
                                <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">📥 Prise (début)</span>
                                <span className="font-medium text-gray-800">{priseLabel}</span>
                              </div>
                              <div className="flex items-start gap-2" data-testid={`liv-remise-${i}`}>
                                <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">📤 Remise (fin)</span>
                                <span className="font-medium text-gray-800">{remiseLabel}</span>
                              </div>
                              {it.livraison_km && nbTrajets > 0 && (
                                <div className="flex items-start gap-2" data-testid={`liv-distance-${i}`}>
                                  <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">📏 Distance</span>
                                  <span className="font-medium text-gray-800">{it.livraison_km} km</span>
                                </div>
                              )}
                              {endroitLabelFmt && nbTrajets > 0 && (
                                <div className="flex items-start gap-2" data-testid={`liv-endroit-${i}`}>
                                  <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">📍 Endroit</span>
                                  <span className="font-medium text-gray-800">{endroitLabelFmt}</span>
                                </div>
                              )}
                              <div className="flex items-start gap-2" data-testid={`liv-trajet-${i}`}>
                                <span className="text-gray-500 min-w-[110px] md:min-w-[140px]">↔️ Type de trajet</span>
                                <span className="font-medium text-gray-800">{sensLabel}</span>
                              </div>
                            </div>
                            {/* Décomposition du prix */}
                            <div className="text-xs text-gray-600 space-y-0.5 pt-2 border-t border-gray-100">
                              {kmMontantAff > 0 && (
                                <div className="flex justify-between" data-testid={`liv-base-${i}`}>
                                  <span>· Frais de base ({it.livraison_km || '—'}{sensCalc && nbTrajets > 0 ? `, ${sensCalc}` : ''})</span>
                                  <span>{kmMontantAff.toFixed(2)} $</span>
                                </div>
                              )}
                              {endroitMontantAff > 0 && (
                                <div className="flex justify-between" data-testid={`liv-supplement-${i}`}>
                                  <span>· Supplément {endroitLabelFmt ? endroitLabelFmt.toLowerCase() : 'endroit précis'}</span>
                                  <span>{endroitMontantAff.toFixed(2)} $</span>
                                </div>
                              )}
                            </div>
                            <div className="flex justify-between items-baseline pt-2 border-t border-gray-200">
                              <div className="text-sm font-semibold text-gray-900">Total livraison</div>
                              <div className="text-sm font-semibold whitespace-nowrap">{livraison.toFixed(2)} $</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Section EXTRAS (si applicable) */}
                      {extras.length > 0 && (
                        <div className="px-4 py-3 border-b" data-testid={`extras-block-${i}`}>
                          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">✨ Extras / Frais supplémentaires</div>
                          <div className="space-y-2">
                            {extras.map((ex, j) => (
                              <div key={j} className="flex justify-between items-start gap-3 text-sm">
                                <div className="text-gray-800 flex-1">{ex.description || ex.nom || '(sans nom)'}</div>
                                <div className="font-medium whitespace-nowrap">{(parseFloat(ex.montant) || 0).toFixed(2)} $</div>
                              </div>
                            ))}
                            <div className="flex justify-between items-baseline pt-1 border-t border-gray-100">
                              <div className="text-sm text-gray-800">Total extras</div>
                              <div className="text-sm font-semibold whitespace-nowrap">{extrasSum.toFixed(2)} $</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Total ligne (équipement) */}
                      <div className="px-4 py-3 bg-gray-50">
                        <div className="flex justify-between items-baseline">
                          <div className="text-sm font-bold text-gray-900">Total équipement</div>
                          <div className="text-base font-bold whitespace-nowrap">{totalLigne.toFixed(2)} $</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Termes et Conditions */}
          {soumission?.termes_conditions && (
            <Card className="mb-2" data-testid="termes-card">
              <CardHeader className="bg-gray-50 border-b py-3">
                <CardTitle className="text-base font-bold">Termes et Conditions</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="soum-riche prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: soumission.termes_conditions }} />
              </CardContent>
            </Card>
          )}

          {/* Totaux */}
          <Card className="mb-2 bg-white text-slate-900" data-testid="totaux-card">
            <CardContent className="pt-6">
              <div className="w-full md:max-w-sm md:ml-auto space-y-2 text-base">
                <div className="flex justify-between">
                  <span className="text-gray-600">Sous-total</span>
                  <span className="font-medium">
                    {soumission?.afficher_rabais === false && (soumission?.montant_rabais_calcule || 0) > 0
                      ? formatMontant((soumission?.sous_total || 0) - (soumission?.montant_rabais_calcule || 0))
                      : formatMontant(soumission?.sous_total)
                    } $
                  </span>
                </div>
                {soumission?.afficher_rabais !== false && (soumission?.montant_rabais_calcule || 0) > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Rabais ({soumission?.rabais_type === 'pourcentage' ? `${soumission?.rabais_valeur}%` : ''})</span>
                    <span>-{formatMontant(soumission.montant_rabais_calcule)} $</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">TPS (5%)</span>
                  <span>{formatMontant(soumission?.tps)} $</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">TVQ (9.975%)</span>
                  <span>{formatMontant(soumission?.tvq)} $</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>TOTAL</span>
                  <span>{formatMontant(soumission?.montant_final)} $</span>
                </div>
                <div className="flex justify-between text-blue-600 pt-2">
                  <span>Depot ({soumission?.depot_type === 'pourcentage' ? `${soumission?.depot_valeur}%` : 'fixe'})</span>
                  <span className="font-semibold">{formatMontant(soumission?.montant_depot_calcule)} $</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Balance</span>
                  <span className="font-semibold">{formatMontant((soumission?.montant_final || 0) - (soumission?.montant_depot_calcule || 0))} $</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section Signature
              🆕 [Phase 79 — 2026-06-22] Refonte UX signature mobile :
                - Carte visuellement distincte sur mobile (border emerald + bg léger)
                - Canvas haute résolution DPR-aware (trait net + suit le doigt)
                - preventDefault sur touch (bloque scroll de page pendant signature)
                - Boutons stack mobile (Confirmer haut, Annuler bas) — évite tap accidentel
                - Input Nom : inputMode + autoComplete + text-base (16px anti-zoom iOS)
                - "Effacer" en lien discret sous le canvas (pas un bouton plein) */}
          <Card
            className="mb-2 bg-white text-slate-900 md:border md:border-slate-200 border-2 border-emerald-200 md:bg-white bg-emerald-50/30"
            data-testid="signature-card"
            ref={signatureSectionRef}
          >
            <CardHeader className="bg-gray-50 border-b py-3">
              <CardTitle className="text-base font-bold text-emerald-700 md:text-slate-900">
                ✍️ Signatures
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-500 italic mb-4">
                J&apos;accepte les termes et conditions decrits dans le present document et consent a acheter les produits et/ou services correspondants.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Signature Client */}
                <div data-testid="client-signature-section">
                  <p className="font-semibold mb-2">Signature Client</p>
                  {signed && soumission?.signature_client_data ? (
                    <div>
                      <div className="inline-block border rounded-lg p-2 bg-white">
                        <img src={soumission.signature_client_data} alt="Signature" className="h-16" />
                      </div>
                      <p className="text-sm font-medium mt-2">{soumission.signature_client_nom}</p>
                      <p className="text-xs text-gray-500">Signe le {new Date(soumission?.date_signature).toLocaleDateString('fr-CA')}</p>
                    </div>
                  ) : showSignature ? (
                    <div className="space-y-4 no-print" data-testid="signature-form">
                      <div>
                        <Label htmlFor="signature-nom" className="text-base">Votre nom complet *</Label>
                        <Input
                          id="signature-nom"
                          value={signatureNom}
                          onChange={(e) => setSignatureNom(e.target.value)}
                          placeholder="Prenom et Nom"
                          className="mt-1 text-base py-3 px-4 min-h-[48px]"
                          inputMode="text"
                          autoComplete="name"
                          autoCapitalize="words"
                          data-testid="signature-nom-input"
                        />
                      </div>
                      <div>
                        <Label className="text-base">Dessinez votre signature *</Label>
                        {/* 🆕 [Phase 79] Canvas wrapper : on retire les dimensions natives
                            <canvas width/height> — elles sont définies en JS via DPR.
                            La hauteur CSS est 140px (vs 120px avant) → plus confortable. */}
                        <div className="border-2 border-dashed border-emerald-300 rounded-lg mt-1 bg-white overflow-hidden">
                          <canvas
                            ref={canvasRef}
                            className="block w-full touch-none cursor-crosshair"
                            style={{ height: '140px' }}
                            data-testid="signature-canvas"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                          />
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-xs text-slate-500 italic">Signez avec votre doigt ou la souris</p>
                          <button
                            type="button"
                            onClick={clearSignature}
                            className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2 transition-colors"
                            data-testid="clear-signature-btn"
                          >
                            Effacer
                          </button>
                        </div>
                      </div>
                      {/* 🆕 [Phase 79] Boutons : ordre DOM = [Annuler, Confirmer].
                          flex-col-reverse mobile → visuel [Confirmer (haut, principal), Annuler (bas, secondaire)]
                          md:flex-row desktop → visuel [Annuler, Confirmer]
                          Gap suffisant (3 mobile, 4 desktop) pour éviter le tap accidentel. */}
                      <div className="flex flex-col-reverse gap-3 md:flex-row md:gap-4 md:justify-end pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowSignature(false)}
                          className="w-full md:w-auto min-h-[48px]"
                          data-testid="cancel-signature-btn"
                        >
                          Annuler
                        </Button>
                        <Button
                          onClick={handleSign}
                          disabled={signing}
                          className="w-full md:w-auto md:min-w-[200px] min-h-[52px] bg-emerald-600 hover:bg-emerald-700 font-semibold text-base"
                          data-testid="confirm-signature-btn"
                        >
                          {signing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Signature en cours...</> : '✓ Confirmer la signature'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="border-b border-black w-48 mt-4 mb-1" />
                      <p className="text-xs text-gray-600">Signature du client</p>
                      <div className="border-b border-black w-32 mt-3 mb-1" />
                      <p className="text-xs text-gray-600">Date</p>
                    </div>
                  )}
                </div>

                {/* Signature Commercant */}
                <div data-testid="merchant-signature-section">
                  <p className="font-semibold mb-2">Signature Commercant</p>
                  {soumission?.signature_commercant_data ? (
                    <div>
                      <div className="inline-block border rounded-lg p-2 bg-white">
                        <img src={soumission.signature_commercant_data} alt="Signature commercant" className="h-16" />
                      </div>
                      <p className="text-sm font-medium mt-2">{soumission.signature_commercant_nom}</p>
                    </div>
                  ) : (
                    <div>
                      <div className="border-b border-black w-48 mt-4 mb-1" />
                      <p className="text-xs text-gray-600">Signature du commercant</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents joints */}
          {documents?.length > 0 && (
            <Card className="mb-2" data-testid="documents-card">
              <CardHeader>
                <CardTitle>Documents joints</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {documents.map((doc, i) => (
                  <div key={doc.id || doc._id || i} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">{doc.nom}</h4>
                    {doc.pdf_url ? (
                      <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm" data-testid={`pdf-link-${doc.code || i}`}>
                        Telecharger le document PDF
                      </a>
                    ) : doc.contenu ? (
                      <div className="soum-riche prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: doc.contenu }} />
                    ) : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Partenaires */}
          {entreprise?.partenaires_urls?.length > 0 && (
            <Card className="mb-2" data-testid="partenaires-card">
              <CardContent className="pt-6 text-center">
                <p className="text-sm font-bold text-gray-500 mb-2 uppercase tracking-wider">Nos partenaires</p>
                <div className="flex items-center justify-center gap-5 flex-wrap">
                  {entreprise.partenaires_urls.map((url, i) => (
                    <img key={url.id || url._id || i} src={url} alt={`Partenaire ${i + 1}`} className="h-9 w-auto object-contain" />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <div className="soum-footer-print-hide text-center text-gray-500 text-sm py-4">
            <p>&copy; {new Date().getFullYear()} {entreprise?.nom_entreprise} - Tous droits reserves</p>
          </div>

          </div>
        </div>
      </div>
    </>
  );
}
